// Read-only refund history for one class payment.
//
// Backed exclusively by public.refunds (admin-only SELECT policy). Renders no
// internal metadata: no attester UUIDs, no Stripe identifiers, no audit rows,
// no ledger internals.

import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { formatCents, historyMethodLabel, historyNote, historyOriginLabel } from "@/lib/refunds";

export interface RefundHistoryRow {
  id: string;
  amount_cents: number;
  channel: string;
  method: string;
  status: string;
  reason: string | null;
  admin_note: string | null;
  historical_import: boolean;
  requested_at: string;
  settled_at: string | null;
}

const statusTone: Record<string, string> = {
  completed: "border-emerald-300 text-emerald-700",
  processing: "border-amber-300 text-amber-700",
  pending: "border-amber-300 text-amber-700",
  failed: "border-destructive text-destructive",
  cancelled: "border-muted text-muted-foreground",
};

const statusLabel = (status: string) =>
  status.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

export function RefundHistory({ paymentId }: { paymentId: string }) {
  const [rows, setRows] = useState<RefundHistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error: err } = await supabase
      .from("refunds")
      .select(
        "id, amount_cents, channel, method, status, reason, admin_note, historical_import, requested_at, settled_at",
      )
      .eq("payment_id", paymentId)
      .order("requested_at", { ascending: true });
    if (err) {
      setError("Refund history couldn't be loaded.");
      setRows([]);
      return;
    }
    setError(null);
    setRows((data ?? []) as RefundHistoryRow[]);
  }, [paymentId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (rows === null) {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading refund history…
      </p>
    );
  }

  if (error) return <p className="text-xs text-muted-foreground">{error}</p>;

  if (rows.length === 0) {
    return <p className="text-xs text-muted-foreground">No refunds on this registration.</p>;
  }

  return (
    <ul className="space-y-2" data-testid="refund-history">
      {rows.map((r) => (
        <li key={r.id} className="rounded-md border bg-background/60 p-2 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">{formatCents(r.amount_cents)}</span>
            <Badge variant="outline" className={`text-[10px] ${statusTone[r.status] ?? ""}`}>
              {statusLabel(r.status)}
            </Badge>
          </div>
          <div className="mt-0.5 text-muted-foreground">
            {new Date(r.settled_at ?? r.requested_at).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}{" "}
            · {historyMethodLabel(r)}
          </div>
          {r.reason && <div className="mt-1">{r.reason}</div>}
          {historyOriginLabel(r) && (
            <div className="mt-0.5 text-[10px] text-muted-foreground">{historyOriginLabel(r)}</div>
          )}
          {historyNote(r) && <div className="mt-0.5 italic text-muted-foreground">{historyNote(r)}</div>}

        </li>
      ))}
    </ul>
  );
}

export default RefundHistory;
